import { useMemo } from 'react';

import { inferSwingPosition } from '@core';
import { BaseNode, Category, ParameterNode } from '@data';

export type UserParameterNodesParams = {
    swingFoundationNodes: BaseNode[] | undefined;
    categories: Category[] | undefined;
    parameterNodes: ParameterNode[] | undefined;
    allUsersParameters: Set<string>;
};

export function useParameterNodes({
    swingFoundationNodes,
    categories,
    parameterNodes,
    allUsersParameters,
}: UserParameterNodesParams) {
    /**
     * Swing Foundation Position Nodes contain titles for each position and act as the main group.
     * P1, P2, P3, etc.
     */
    const swingFoundationPositionNodes = useMemo(
        () =>
            (swingFoundationNodes ?? []).reduce<Record<string, BaseNode>>((acc, node) => {
                const swingPosition = inferSwingPosition(node.id);
                if (!swingPosition) {
                    return acc;
                }
                acc[swingPosition] = node;
                return acc;
            }, {}),
        [swingFoundationNodes],
    );

    /**
     * Each swing position tab has multiple tables of parameters.
     * Each table is a category, which can contain multiple parameters.
     *
     * We create a map with Position (p1, p2, p3, ...) as key and category[] (table) as value.
     */
    const swingFoundationParameterCategories = useMemo(() => {
        return categories
            ?.sort((a, b) => a.displayOrder - b.displayOrder)
            ?.reduce(
                (acc, group) => {
                    const position = group.id.match(/.(p\d+)/)?.[1];
                    if (!position) return acc;

                    if (!acc[position]) {
                        acc[position] = [];
                    }

                    acc[position].push(group);
                    return acc;
                },
                {} as Record<string, Category[]>,
            );
    }, [categories]);
    /**
     * This map is category -> parameter node [] map,
     * which we utilize to render individual row for each category.
     */
    const groupParameterMap = useMemo(() => {
        return parameterNodes
            ?.filter((node) => allUsersParameters.has(node.id))
            .reduce<Record<string, ParameterNode[]>>((acc, node) => {
                const groupId = node.categories.swingFoundationGroup;
                if (!groupId) {
                    return acc;
                }

                if (!acc[groupId]) {
                    acc[groupId] = [];
                }

                acc[groupId].push(node);

                return acc;
            }, {});
    }, [parameterNodes, allUsersParameters]);

    return {
        swingFoundationPositionNodes,
        swingFoundationParameterCategories,
        groupParameterMap,
    };
}
